// src/components/EmailServices/List.js

import React, { useState, useEffect, useCallback } from 'react';
import { Lock, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './List.css';

const API_URL = process.env.REACT_APP_API_URL;
const MySwal = withReactContent(Swal);

// --- FIX: Define the base path for this component's API calls ---
const LISTS_API_PATH = `${API_URL}/api/lists`;


const List = () => {
  const [lists, setLists] = useState([]);
  const [usersInList, setUsersInList] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);

  const fetchLists = useCallback(async () => {
    // Only show the full-page loader on the initial fetch
    if (!lists.length) {
      setLoading(true);
    }
    try {
      // FIX: Use the correct, full API path
      const response = await fetch(LISTS_API_PATH);
      if (!response.ok) throw new Error('Failed to fetch lists.');
      const data = await response.json();
      console.log("Data received from API:", data); 
      setLists(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch lists:", err);
    } finally {
      setLoading(false);
    }
  }, [lists.length]);

  useEffect(() => {
    fetchLists();
    // Optional: Add a listener to refetch on window focus
    window.addEventListener('focus', fetchLists);
    return () => {
      window.removeEventListener('focus', fetchLists);
    };
  }, [fetchLists]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewListName('');
  };

  const handleAddList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) {
      MySwal.fire({ icon: 'error', title: 'Oops...', text: 'List name cannot be empty.' });
      return;
    }
    try {
      // FIX: Use the correct, full API path
      const response = await fetch(LISTS_API_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newListName }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create list.');

      MySwal.fire({ icon: 'success', title: 'List Created!', text: `The list "${newListName}" has been added.`, timer: 2000, showConfirmButton: false });
      // Refetch to get the most up-to-date list from the server
      fetchLists();
      handleCloseModal();
    } catch (err) {
      MySwal.fire({ icon: 'error', title: 'Creation Failed', text: err.message });
    }
  };

  const handleSelectList = async (list) => {
    try {
      setLoading(true);
      setSelectedList(list);
      // FIX: Use the correct, full API path
      const response = await fetch(`${LISTS_API_PATH}/${list._id}/users`);
      if (!response.ok) throw new Error(`Failed to fetch users for ${list.name}.`);
      const usersData = await response.json();
      setUsersInList(usersData);
      setError(null);
    } catch (err) {
      setError(err.message);
      setSelectedList(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLists = () => {
    setSelectedList(null);
    setUsersInList([]);
    setSelectedUsers([]);
    setError(null);
    fetchLists(); // Refetch lists to show updated user counts
  };

  const handleBulkDelete = async (idsToDelete = selectedUsers) => {
    if (idsToDelete.length === 0 || !selectedList) return;
    MySwal.fire({
      title: 'Are you sure?',
      text: `You are about to remove ${idsToDelete.length} user(s). This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove them!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // FIX: Use the correct, full API path
          const response = await fetch(`${LISTS_API_PATH}/${selectedList._id}/users/bulk-delete`, {
            method: 'POST', // Should ideally be DELETE, but POST works.
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userIds: idsToDelete }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.message || 'Failed to remove users.');
          
          // Refresh the user list for the selected list
          handleSelectList(selectedList); 
          setSelectedUsers([]); // Clear selection
          MySwal.fire('Removed!', 'The selected user(s) have been removed.', 'success');
        } catch (err) {
          MySwal.fire({ icon: 'error', title: 'Removal Failed', text: err.message });
        }
      }
    });
  };

  const handleDeleteList = (listToDelete) => {
    MySwal.fire({
      title: 'Delete this list?',
      text: `You are about to permanently delete "${listToDelete.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // FIX: Use the correct, full API path
          const res = await fetch(`${LISTS_API_PATH}/${listToDelete._id}`, { method: 'DELETE' });
          const body = await res.json();
          if (!res.ok) throw new Error(body.message || 'Failed to delete list');
          
          fetchLists(); // Refetch all lists
          MySwal.fire('Deleted!', body.message, 'success');
        } catch (err) {
          console.error('List deletion failed:', err);
          MySwal.fire('Error', err.message, 'error');
        }
      }
    });
  };
  
  // ... The rest of your component (handleRemoveUser, render functions, etc.) is fine.
  const handleRemoveUser = (userIdToRemove) => {
    handleBulkDelete([userIdToRemove]);
  };
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === usersInList.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usersInList.map(user => user.id));
    }
  };

  if (loading && !selectedList && !lists.length) {
    return <div className="email-lists__message"><h1>Loading Lists...</h1></div>;
  }
  if (error) {
    return <div className="email-lists__message email-lists__message--error"><h1>Error: {error}</h1><button onClick={fetchLists} className='email-lists__btn'>Try Again</button></div>;
  }

  const renderListsTable = () => (
    <>
      <div className="email-lists__header">
        {/* <h1>Email Lists</h1> */}
        <button onClick={handleOpenModal} className="email-lists__btn email-lists__btn--primary"><Plus size={16} /> Add List</button>
      </div>
      <div className="email-lists__table-wrapper">
        <table className="email-lists__table">
          <thead>
            <tr>
              <th>List Name</th>
              <th>Users</th>
              <th>Last Sync</th>
              <th className="email-lists__action-col">Action</th>
            </tr>
          </thead>
          <tbody>
            {lists.length > 0 ? lists.map((list) => (
              <tr key={list._id}>
                <td data-label="List Name" className="email-lists__cell--clickable" onClick={() => handleSelectList(list)} title="Click to view users">
                  {list.name}
                  {list.type === 'system' && <Lock size={14} className="email-lists__lock-icon" title="This is a system list and cannot be deleted." />}
                </td>
                <td data-label="Users" onClick={() => handleSelectList(list)}>{list.totalUsers}</td>
                <td data-label="Last Sync" onClick={() => handleSelectList(list)}>{new Date(list.lastSync).toLocaleString()}</td>
                <td data-label="Action" className="email-lists__action-col">
                  {list.isDeletable ? (
                    <button className="email-lists__btn email-lists__btn--danger-text" onClick={(e) => { e.stopPropagation(); handleDeleteList(list); }}>Delete</button>
                  ) : ( <span className="email-lists__no-action">-</span> )}
                </td>
              </tr>
            )) : (
              <tr><td colSpan="4" className="email-lists__empty-row">No lists created yet. Click 'Add List' to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderUsersTable = () => {
    const isSystemList = selectedList.type === 'system';
    return (
      <>
        <div className="email-lists__header">
          <div className="email-lists__header-group">
            <button onClick={handleBackToLists} className="email-lists__btn email-lists__btn--secondary"><ArrowLeft size={16} /> Back</button>
            <h1>Users in "{selectedList.name}"</h1>
          </div>
          {!isSystemList && selectedUsers.length > 0 && (
            <div className="email-lists__bulk-actions">
              <span>{selectedUsers.length} selected</span>
              <button onClick={() => handleBulkDelete()} className="email-lists__btn email-lists__btn--danger"><Trash2 size={16} /> Delete Selected</button>
            </div>
          )}
        </div>
        {loading ? <div className="email-lists__message"><p>Loading users...</p></div> : (
        <div className="email-lists__table-wrapper">
          <table className="email-lists__table">
            <thead>
              <tr>
                <th className="email-lists__checkbox-col"><input type="checkbox" onChange={handleSelectAllUsers} checked={usersInList.length > 0 && selectedUsers.length === usersInList.length} disabled={usersInList.length === 0 || isSystemList} /></th>
                <th>S/N</th>
                <th>User ID</th>
                <th>Name</th>
                <th>Email</th>
                <th className="email-lists__action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {usersInList.length > 0 ? (
                usersInList.map((user, index) => (
                  <tr key={user.id} className={selectedUsers.includes(user.id) ? 'email-lists__row--selected' : ''}>
                    <td className="email-lists__checkbox-col" data-label=""><input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleSelectUser(user.id)} disabled={isSystemList} /></td>
                    <td data-label="S/N">{index + 1}</td>
                    <td data-label="User ID">{user.userId}</td>
                    <td data-label="Name">{user.name}</td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Action" className="email-lists__action-col">
                      {!isSystemList ? ( <button onClick={() => handleRemoveUser(user.id)} className="email-lists__btn email-lists__btn--danger-text">Remove</button>
                      ) : ( <span className="email-lists__no-action">-</span> )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="email-lists__empty-row">No users found in this list.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </>
    );
  };
  
    return (
    <div className="email-lists">
      {selectedList ? renderUsersTable() : renderListsTable()}
      {isModalOpen && (
        <div className="email-lists-modal__overlay" onClick={handleCloseModal}>
          <div className="email-lists-modal__content" onClick={e => e.stopPropagation()}>
            <h2>Create a New List</h2>
            <form onSubmit={handleAddList}>
              <label htmlFor="listName">List Name</label>
              <input type="text" id="listName" value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="e.g., 'Monthly Promos'" required autoFocus />
              <div className="email-lists-modal__actions">
                <button type="button" onClick={handleCloseModal} className="email-lists__btn email-lists__btn--secondary">Cancel</button>
                <button type="submit" className="email-lists__btn email-lists__btn--primary">Add List</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default List;